import { createEffect, on, splitProps, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import { formatDate, isoDate, parseDateInput } from "@/utils/date"
import "./date-input.css"

/** Display day-first text while retaining the native date input's ISO event contract. */
export function DateInput(props: Omit<JSX.InputHTMLAttributes<HTMLInputElement>, "type" | "value"> & { value?: string }) {
  const language = useLanguage()
  const [local, rest] = splitProps(props, ["value", "onInput", "onChange", "ref", "name", "id", "class", "placeholder"])
  const [state, setState] = createStore({ text: local.value ? formatDate(local.value) : "", invalid: false })
  let text!: HTMLInputElement
  let native!: HTMLInputElement
  let emitted: string | undefined
  let typing = false
  const display = (value: string) => value ? formatDate(value) : ""
  const validate = () => {
    const value = parseDateInput(state.text)
    const invalid = !!state.text && (!value || (!!props.min && value < String(props.min)) || (!!props.max && value > String(props.max)))
    setState("invalid", invalid)
    text.setCustomValidity(invalid ? language.t("books.dateError", {
      start: formatDate(String(props.min || "0001-01-01")), end: formatDate(String(props.max || "9999-12-31")),
    }) : "")
  }
  createEffect(on(() => local.value, (value) => {
    if (value !== emitted) setState("text", display(value ?? ""))
    emitted = undefined
  }))
  createEffect(() => { props.min; props.max; state.text; validate() })
  const acceptNative: JSX.InputEventHandler<HTMLInputElement, InputEvent> = (event) => {
    if (!typing) setState("text", display(event.currentTarget.value))
    emitted = event.currentTarget.value
    const handler = local.onInput
    if (typeof handler === "function") handler(event)
    else if (handler) handler[0](handler[1], event)
  }
  return <span class="app-date-input">
    <input {...rest} id={local.id} ref={(element) => {
      text = element
      if (typeof local.ref === "function") local.ref(element)
    }} type="text" data-date-input class={local.class} value={state.text} inputMode="numeric" autocomplete="off"
      placeholder={local.placeholder ?? language.t("cowork.accountingNav.dateFormat")}
      aria-invalid={props["aria-invalid"] || state.invalid || undefined}
      onInput={(event) => {
        setState("text", event.currentTarget.value)
        native.value = parseDateInput(state.text)
        if (native.value) setState("text", display(native.value))
        typing = true
        try { native.dispatchEvent(new InputEvent("input", { bubbles: true, inputType: event.inputType })) }
        finally { typing = false }
      }} onChange={() => native.dispatchEvent(new Event("change", { bubbles: true }))}
      onBlur={() => { const value = parseDateInput(state.text); if (value) setState("text", display(value)) }} />
    <input ref={native} type="date" class="app-date-input-native" tabindex={-1} aria-hidden="true"
      name={local.name} value={isoDate(local.value ?? "")} min={props.min} max={props.max} disabled={props.disabled}
      readOnly={props.readOnly} onInput={acceptNative} onChange={local.onChange} />
    <button type="button" class="app-date-input-picker" disabled={props.disabled || props.readOnly}
      aria-label={language.t("books.date")} aria-haspopup="dialog" onClick={(event) => {
        event.preventDefault()
        if (!native.showPicker) { text.focus(); return }
        try { native.showPicker() } catch { text.focus() }
      }}>
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" />
      </svg>
    </button>
  </span>
}
